import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import DashboardLayout from '@/components/layout/DashboardLayout';

interface AffiliateRow {
  user_id: string;
  custom_code: string;
  is_active: boolean;
  total_referrals?: number;
  total_earnings?: number;
}

export default function AffiliatesTable() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const { data, isLoading } = useQuery({
    queryKey: ['admin-affiliates', page, search],
    queryFn: async () => {
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      // Base list from affiliate_codes
      let base = supabase
        .from('affiliate_codes')
        .select('user_id, custom_code, is_active', { count: 'exact' })
        .order('updated_at', { ascending: false })
        .range(from, to);

      if (search) {
        base = base.ilike('custom_code', `%${search}%`);
      }

      const { data: codes, count } = await base as any;

      // For each, compute totals
      const rows: AffiliateRow[] = [];
      for (const c of (codes || [])) {
        const { count: total_referrals } = await supabase
          .from('referrals')
          .select('id', { count: 'exact' })
          .eq('affiliate_code', c.custom_code.toLowerCase());

        const { data: earnings } = await supabase
          .from('commission_earnings')
          .select('commission_amount')
          .eq('affiliate_user_id', c.user_id);

        const total_earnings = (earnings || []).reduce((s, e: any) => s + Number(e.commission_amount || 0), 0);

        rows.push({
          user_id: c.user_id,
          custom_code: c.custom_code,
          is_active: c.is_active,
          total_referrals: total_referrals || 0,
          total_earnings,
        });
      }

      return { rows, count: count || 0 };
    },
  });

  const totalPages = useMemo(() => Math.max(1, Math.ceil((data?.count || 0) / pageSize)), [data?.count]);

  return (
    <DashboardLayout title="Affiliates" maxWidth="7xl">
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2 w-full justify-end">
          <Input placeholder="Search code…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="w-64" />
        </div>
      </div>

        <div className="border rounded-lg">
          {isLoading ? (
            <div className="p-4 space-y-2">
              <Skeleton className="h-5 w-1/3" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Affiliate Code</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Active</TableHead>
                <TableHead>Referrals</TableHead>
                <TableHead>Total Earned</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.rows || []).map((a) => (
                <TableRow key={a.custom_code} className="cursor-pointer" onClick={() => navigate(`/admin/business/affiliates/${a.user_id}`)}>
                  <TableCell className="font-medium">{a.custom_code}</TableCell>
                  <TableCell className="font-mono text-xs">{a.user_id}</TableCell>
                  <TableCell>{a.is_active ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{a.total_referrals}</TableCell>
                  <TableCell>${(a.total_earnings || 0).toFixed(2)}</TableCell>
                </TableRow>
              ))}
              {(!isLoading && (data?.rows || []).length === 0) && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">No affiliates found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          )}
        </div>

        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">Page {page} of {totalPages}</div>
          <div className="flex gap-2">
            <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</Button>
            <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</Button>
          </div>
        </div>
    </DashboardLayout>
  );
}


